import React, { useContext } from "react";
import { AnswerFormat, FormStepDefinition, SignatureAnswerFormat, SurveyDefinition, TextChoiceAnswerFormat, ValuePickerAnswerFormat } from "../../../../types/Survey";
import { ConsentSignatureResultModel, QuestionResultModel, ResultModel } from "../../../../types/SurveyResult";
import SurveyMarkdown from "../SurveyMarkdown/SurveyMarkdown";
import { isMultilineFormItem } from "../../Util/formLayout";
import { SurveyStepContext } from "../../Util/surveyStepContext";
import ReactDOMServer from "react-dom/server";
import { getTextStyles } from "../../Util/surveyStyles";
import { TranslateContext } from "../../../../widgets/MyDataHelps/TranslateContext";

// HACK typescript doesn't seem to understand the raw-loader syntax 
// @ts-ignore
import rawSurveyFormCss from '!!raw-loader!./FormStepToHtml.css';
// @ts-ignore
import rawSurveyMarkdownCss from '!!raw-loader!../SurveyMarkdown/SurveyMarkdown.css';

export function renderFormStepToHtml(surveyDefinition: SurveyDefinition, stepDefinition: FormStepDefinition, results: ResultModel[], translate: any) {
    return ReactDOMServer.renderToStaticMarkup(<TranslateContext.Provider value={translate}>
        <FormStepToHtml surveyDefinition={surveyDefinition} stepDefinition={stepDefinition} results={results} />
    </TranslateContext.Provider>);
}

export interface FormStepToHtmlProps {
    results: ResultModel[];
    surveyDefinition: SurveyDefinition;
    stepDefinition: FormStepDefinition;
}

export default function FormStepToHtml(props: FormStepToHtmlProps) {
    return <SurveyStepContext.Provider value={{
        surveyDefinition: props.surveyDefinition,
        stepDefinition: props.stepDefinition,
        mode: "Survey"
    }}>
        <div className="form-step-to-html">
            <style type="text/css" dangerouslySetInnerHTML={{ __html: rawSurveyFormCss }} />
            <style type="text/css" dangerouslySetInnerHTML={{ __html: rawSurveyMarkdownCss }} />
            {props.stepDefinition.title &&
                <div className="form-step-to-html-title" style={getTextStyles("title", props.surveyDefinition, props.stepDefinition, true)}>
                    <SurveyMarkdown format="GFM" text={props.stepDefinition.title || ""} />
                </div>
            }
            {props.stepDefinition.text &&
                <div className="form-step-to-html-text" style={getTextStyles("text", props.surveyDefinition, props.stepDefinition, true)}>
                    <SurveyMarkdown format="GFM" text={props.stepDefinition.text || ""} />
                </div>
            }
            <div className="form-step-to-html-survey-form">
                {props.stepDefinition.formItems
                    .filter(item => props.results.some(r => r.Identifier == item.identifier))
                    .map((item) =>
                        <FormItemToHtml
                            identifier={item.identifier}
                            key={item.identifier}
                            answerFormat={item.answerFormat}
                            label={item.text}
                            result={props.results.find(r => r.Identifier == item.identifier)}
                            labelPosition={isMultilineFormItem(item, "v2") ? "top" : "left"}
                        />
                    )}
            </div>
            {props.stepDefinition.footnote &&
                <div className="form-step-to-html-footnote" style={getTextStyles("footnote", props.surveyDefinition, props.stepDefinition, true)}>
                    <SurveyMarkdown format="GFM" text={props.stepDefinition.footnote || ""} />
                </div>
            }
        </div>
    </SurveyStepContext.Provider>
}

export interface FormItemToHtmlProps {
    identifier: string;
    answerFormat?: AnswerFormat;
    label?: string;
    result?: ResultModel;
    labelPosition?: "top" | "left";
}

export function FormItemToHtml(props: FormItemToHtmlProps) {
    const translate = useContext(TranslateContext);

    if (!props.answerFormat) {
        return <div className="form-item-to-html">
            <label className="form-item-to-html-label">
                <SurveyMarkdown inline text={props.label || ""} />
            </label>
            <></>
        </div>
    }

    if (props.answerFormat.type == "TextChoiceAnswerFormat") {
        const textChoiceAnswerFormat = props.answerFormat as TextChoiceAnswerFormat;
        const choices = (props.result as QuestionResultModel | undefined)?.Choices || [];
        return <div className="form-item-to-html">
            <label className="form-item-to-html-label">
                <SurveyMarkdown inline text={props.label || ""} />
            </label>
            {textChoiceAnswerFormat.textChoices.map((choice, index) => (
                <div key={index} className="form-item-to-html-text-choice-item">
                    <input
                        type={textChoiceAnswerFormat.style.startsWith("Single") ? "radio" : "checkbox"}
                        id={`${props.identifier}-${choice.value}`}
                        name={props.identifier}
                        value={choice.value}
                        checked={choices.includes(choice.value)}
                        readOnly
                    />
                    <label htmlFor={`${props.identifier}-${choice.value}`}>
                        <SurveyMarkdown text={choice.text} />
                        {choice.description && <div className="form-item-to-html-text-choice-description">
                            <SurveyMarkdown text={choice.description} />
                        </div>}
                    </label>
                </div>
            ))}
        </div>;
    }

    if (props.answerFormat.type == "BooleanAnswerFormat") {
        const possibleChoices = [
            { value: "True", text: translate.instant("yes-label-text") },
            { value: "False", text: translate.instant("no-label-text") }
        ];
        const selectedChoices = [(props.result as QuestionResultModel | undefined)?.Answer];
        return <div className="form-item-to-html">
            <label className="form-item-to-html-label">
                <SurveyMarkdown inline text={props.label || ""} />
            </label>
            {possibleChoices.map((choice, index) => (
                <div key={index} className="form-item-to-html-text-choice-item">
                    <input
                        type={"radio"}
                        id={`${props.identifier}-${choice.value}`}
                        name={props.identifier}
                        value={choice.value}
                        checked={selectedChoices.includes(choice.value)}
                        readOnly
                    />
                    <label htmlFor={`${props.identifier}-${choice.value}`}>
                        <SurveyMarkdown text={choice.text} inline />
                    </label>
                </div>
            ))}
        </div>;
    }

    if (props.answerFormat.type == "SignatureAnswerFormat") {
        const signatureAnswerFormat = props.answerFormat as SignatureAnswerFormat;
        const signatureResult = props.result as ConsentSignatureResultModel | undefined;
        return <div className="form-item-to-html">
            <label className="form-item-to-html-label">
                <SurveyMarkdown inline text={props.label || ""} />
            </label>
            <div className="signature-to-html">
                {signatureAnswerFormat.requiresName &&
                    <div className="signature-to-html-element">
                        <span className="signature-to-html-value">
                            {signatureResult?.Signature?.GivenName ?? <>&nbsp;</>} {signatureResult?.Signature?.FamilyName}
                        </span>
                        <label className="signature-to-html-label">
                            {signatureAnswerFormat.pdfNameLineLabel || translate.instant("consent-printed-name-label", { signatureLabel: signatureAnswerFormat.signatureTitle || signatureAnswerFormat.signatureIdentifier })}
                        </label>
                    </div>
                }
                {signatureAnswerFormat.requiresSignatureImage &&
                    <div className="signature-to-html-element">
                        <span className="signature-to-html-value">
                            {signatureResult?.Signature.HandwrittenSignatureBase64 &&
                                <img src={signatureResult.Signature.HandwrittenSignatureBase64} alt="Signature" />
                            }
                            {!signatureResult?.Signature.HandwrittenSignatureBase64 && <>&nbsp;</>}
                        </span>
                        <label className="signature-to-html-label">
                            {signatureAnswerFormat.pdfSignatureLineLabel || translate.instant("consent-signature-label", { signatureLabel: signatureAnswerFormat.signatureTitle || signatureAnswerFormat.signatureIdentifier })}
                        </label>
                    </div>
                }
                <div className="signature-to-html-element">
                    <span className="signature-to-html-value">
                        {signatureResult?.Signature.Date ?? <>&nbsp;</>}
                    </span>
                    <label className="signature-to-html-label">
                        {translate.instant("consent-date-label")}
                    </label>
                </div>
            </div>
        </div>;
    }

    const resultModel = props.result as QuestionResultModel | undefined;
    let answerString = resultModel?.Answer || resultModel?.Choices?.[0] || "";
    if (props.answerFormat.type == "ValuePickerAnswerFormat") {
        const choices = (props.answerFormat as ValuePickerAnswerFormat).textChoices;
        answerString = choices.find(c => c.value === answerString)?.text || "";
    }

    return <div className="form-item-to-html">
        <label className="form-item-to-html-label">
            <SurveyMarkdown inline text={props.label || ""} />
        </label>
        <input type="text"
            value={answerString}
            readOnly
            className="form-item-to-html-value"
        />
    </div>
}
